import { defineMessages } from 'react-intl';

export default defineMessages({
  title: {
    id: 'ContractPriceCheckTool.title',
    defaultMessage: 'Contract Price Check Tool',
    description: 'Page title of the contract price check tool'
  },
  description: {
    id: 'ContractPriceCheckTool.description',
    defaultMessage:
      'Ask Datahub directly what contract prices it returns for a location, one or more part numbers, customer email, and catalog. Datahub is always called, even if external pricing is disabled.',
    description: 'Description shown under the contract price check tool title'
  },
  locationLabel: {
    id: 'ContractPriceCheckTool.location.label',
    defaultMessage: 'Location',
    description: 'Label of the location field'
  },
  locationPlaceholder: {
    id: 'ContractPriceCheckTool.location.placeholder',
    defaultMessage: 'Search by name, or enter e.g. D-0123-45',
    description: 'Placeholder of the location field'
  },
  locationsLoading: {
    id: 'ContractPriceCheckTool.location.loading',
    defaultMessage: 'Loading locations...',
    description: 'Shown under the location field while the location list loads'
  },
  locationsLoadFailed: {
    id: 'ContractPriceCheckTool.location.loadFailed',
    defaultMessage:
      'Could not load the location list. Enter a location ID manually (e.g. D-0123-45).',
    description:
      'Shown under the location field when the location list fails to load'
  },
  locationUnknown: {
    id: 'ContractPriceCheckTool.location.unknown',
    defaultMessage:
      'Not a known location. It will be sent to Datahub as entered.',
    description:
      'Shown under the location field when the entered ID does not match a known location'
  },
  locationWithCurrency: {
    id: 'ContractPriceCheckTool.location.withCurrency',
    defaultMessage: '{name} · {currency}',
    description:
      "Shown under the location field: the location's name and its default currency"
  },
  locationWithAssumedCurrency: {
    id: 'ContractPriceCheckTool.location.withAssumedCurrency',
    defaultMessage: '{name} · {currency} (assumed)',
    description:
      'Shown under the location field when the location has no usable default currency, so prices will be shown in this fallback currency'
  },
  locationInactive: {
    id: 'ContractPriceCheckTool.location.inactive',
    defaultMessage: '{name} (inactive)',
    description: 'Display name of a deactivated location'
  },
  partNumberLabel: {
    id: 'ContractPriceCheckTool.partNumber.label',
    defaultMessage: 'Part Numbers',
    description: 'Label of the part number field'
  },
  partNumberPlaceholder: {
    id: 'ContractPriceCheckTool.partNumber.placeholder',
    defaultMessage: 'e.g. 12345, 67890',
    description: 'Placeholder of the part number field'
  },
  partNumberHint: {
    id: 'ContractPriceCheckTool.partNumber.hint',
    defaultMessage: 'Separate multiple part numbers with commas.',
    description:
      'Shown under the part number field to explain that it accepts a comma-separated list'
  },
  emailLabel: {
    id: 'ContractPriceCheckTool.email.label',
    defaultMessage: 'User Email',
    description: 'Label of the customer email field'
  },
  emailPlaceholder: {
    id: 'ContractPriceCheckTool.email.placeholder',
    defaultMessage: 'e.g. user@example.com',
    description: 'Placeholder of the customer email field'
  },
  catalogLabel: {
    id: 'ContractPriceCheckTool.catalog.label',
    defaultMessage: 'Catalog',
    description: 'Label of the catalog field'
  },
  catalogAurora: {
    id: 'ContractPriceCheckTool.catalog.aurora',
    defaultMessage: 'Aurora',
    description: 'Catalog option for the Aurora catalog'
  },
  catalogDealer: {
    id: 'ContractPriceCheckTool.catalog.dealer',
    defaultMessage: 'Dealer',
    description: 'Catalog option for the dealer catalog'
  },
  required: {
    id: 'ContractPriceCheckTool.validation.required',
    defaultMessage: 'Required',
    description: 'Validation message for a required field left blank'
  },
  clear: {
    id: 'ContractPriceCheckTool.clear',
    defaultMessage: 'Clear',
    description: 'Button that resets the form'
  },
  submit: {
    id: 'ContractPriceCheckTool.submit',
    defaultMessage: 'Check Pricing',
    description: 'Button that submits the price check'
  },
  submitting: {
    id: 'ContractPriceCheckTool.submitting',
    defaultMessage: 'Checking...',
    description: 'Submit button label while the price check is running'
  },
  requestFailed: {
    id: 'ContractPriceCheckTool.error.requestFailed',
    defaultMessage: 'An error occurred while checking the contract price.',
    description: 'Fallback error message when the request fails without details'
  },
  upstreamStatus: {
    id: 'ContractPriceCheckTool.error.upstreamStatus',
    defaultMessage: 'Datahub status: {status}',
    description: 'HTTP status returned by Datahub for a failed request'
  },
  upstreamBody: {
    id: 'ContractPriceCheckTool.error.upstreamBody',
    defaultMessage: 'Datahub response body',
    description:
      'Heading of the raw body returned by Datahub for a failed request'
  },
  emptyUpstreamBody: {
    id: 'ContractPriceCheckTool.error.emptyUpstreamBody',
    defaultMessage: '(empty)',
    description: 'Shown when Datahub returned an error with an empty body'
  },
  summaryTitle: {
    id: 'ContractPriceCheckTool.summary.title',
    defaultMessage: 'Result',
    description: 'Heading of the result summary'
  },
  datahubError: {
    id: 'ContractPriceCheckTool.summary.datahubError',
    defaultMessage: 'Datahub reported an error: {error}',
    description: 'Shown when the Datahub response contains an error'
  },
  noPrice: {
    id: 'ContractPriceCheckTool.summary.noPrice',
    defaultMessage: 'Datahub returned no prices for these parts.',
    description: 'Shown when the Datahub response has no price data'
  },
  partNumberColumn: {
    id: 'ContractPriceCheckTool.summary.partNumber',
    defaultMessage: 'Part Number',
    description: 'Result summary column for the part number'
  },
  contractPriceColumn: {
    id: 'ContractPriceCheckTool.summary.contractPrice',
    defaultMessage: 'Contract Price',
    description: 'Result summary column for the contract price'
  },
  locationOverridePriceColumn: {
    id: 'ContractPriceCheckTool.summary.locationOverridePrice',
    defaultMessage: 'Location Override Price',
    description: 'Result summary column for the location override price'
  },
  suggestedRetailPriceColumn: {
    id: 'ContractPriceCheckTool.summary.suggestedRetailPrice',
    defaultMessage: 'Suggested Retail Price',
    description: 'Result summary column for the suggested retail price'
  },
  noValue: {
    id: 'ContractPriceCheckTool.summary.noValue',
    defaultMessage: '—',
    description: 'Shown in the result summary when a price is missing'
  },
  waiting: {
    id: 'ContractPriceCheckTool.waiting',
    defaultMessage: 'Waiting for response... {ms, number} ms',
    description: 'Live elapsed time shown while a price check is running'
  },
  roundTrip: {
    id: 'ContractPriceCheckTool.roundTrip',
    defaultMessage: 'Round trip: {ms, number} ms',
    description:
      'Browser-side time between sending the price check and receiving the result'
  },
  rawResponseTitle: {
    id: 'ContractPriceCheckTool.rawResponse.title',
    defaultMessage: 'Raw Response',
    description: 'Heading of the raw JSON response from Datahub'
  }
});
